'use client';

/**
 * Branded Customer Login Page
 *
 * Customer-facing magic-link sign-in surface, branded per organisation slug.
 * Path-based slug (`/customer/login/<org-slug>`) — the inverse of the staff
 * login rule:
 *
 *   - Staff/admin login (`/admin/login/<slug>`) is single-tenanted at the
 *     *root parent* level — child slugs walk up to the root parent.
 *   - Customer login here is single-tenanted at the *child* level —
 *     customers are invited into specific child orgs (eg `pennywise`), so
 *     if the slug resolves to a root parent (`parent_org_id IS NULL`) we
 *     `router.replace` to the slug-less `/customer/login` notice.
 *
 * Sign-in is **magic-link only** — social / OAuth providers are not
 * exposed on customer surfaces regardless of org settings. Staff SSO
 * lives on the branded admin login.
 */
import { c as _c } from "react/compiler-runtime";
import React, { useState, useEffect, Suspense } from 'react';
import { useRouter, useSearchParams, useParams, usePathname } from 'next/navigation';
import Link from 'next/link';
import { supabase } from '@/lib/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { GlassCard, GlassCardContent, GlassCardDescription, GlassCardHeader, GlassCardTitle } from '@/components/ui/glass-card';
import { ThemeToggle } from '@/components/ui/theme-toggle';
import { Mail, CheckCircle, Loader2, Sparkles, Shield, Zap, Lock, LogOut, UserCircle2, UserPlus } from 'lucide-react';
import { buildBrandStyle } from '@/lib/ui/brand';
import { useFavicon } from '@/lib/ui/useFavicon';
import TurnstileWidget from '@/components/auth/TurnstileWidget';
import { MarkdownText } from '@/components/ui/markdown-text';
import { AlertModal } from '@/components/ui/modal';
import { getIdentityFieldCopy, getAuthJourneyCopy } from '@/lib/auth/identityFieldCopy';
import { getAuthChallengeSettings, formatExpiryMinutes } from '@/lib/auth/authChallengeSettings';
import SmsCodeEntry from '@/components/auth/SmsCodeEntry';
import CustomerPasswordInput from '@/components/auth/CustomerPasswordInput';
import CustomerForgotPassword from '@/components/auth/CustomerForgotPassword';
import { getCustomerAuthModel } from '@/lib/auth/customerAuthModel';
import { getCustomerPasswordPolicy } from '@/lib/auth/customerPasswordPolicy';
import PoweredByPaylinkFooter from '@/components/customer/PoweredByPaylinkFooter';
import { getOrgLegalContent, type InvitationChannelMode } from '@/types/organization';
import { isPhoneShadowEmail } from '@/lib/referral/phoneShadowEmail';

/** Query parameter carried by the model 2 reset email (`RESET_LINK_PARAM` server-side). */
const RESET_LINK_PARAM = 'reset_token';

// Animated background driven by org brand CSS custom properties so the
// blurred blobs reflect the org's primary / secondary colours. Mirrors
// the admin branded-login implementation (`app/admin/login/[org-slug]`).
// Falls back to the default brand from `lib/ui/brand.ts` when no org is
// loaded yet.
function AnimatedBackground() {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="absolute -top-1/2 -left-1/4 h-[800px] w-[800px] rounded-full blur-[120px] animate-pulse" style={{
      background: "radial-gradient(circle, rgb(var(--org-primary-rgb) / 0.2) 0%, rgb(var(--org-primary-rgb) / 0.15) 40%, transparent 70%)"
    }} />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="absolute -bottom-1/2 -right-1/4 h-[600px] w-[600px] rounded-full blur-[100px] animate-pulse" style={{
      background: "radial-gradient(circle, rgb(var(--org-secondary-rgb) / 0.2) 0%, rgb(var(--org-secondary-rgb) / 0.1) 40%, transparent 70%)",
      animationDelay: "1s"
    }} />;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="fixed inset-0 -z-10 overflow-hidden">{t0}{t1}<div className="absolute top-1/3 right-1/4 h-[400px] w-[400px] rounded-full blur-[80px] animate-pulse" style={{
        background: "radial-gradient(circle, rgb(var(--org-primary-rgb) / 0.1) 0%, transparent 70%)",
        animationDelay: "2s"
      }} /></div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}

// Static fallback shown on the left when the child org has not authored
// any `landing_page_content`. Mirrors the original hero so orgs that
// haven't filled in a landing blurb still see a complete-looking page.
function FallbackHeroBody() {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [{
      icon: Shield,
      text: "Secure passwordless login"
    }, {
      icon: Zap,
      text: "Instant access to your account"
    }, {
      icon: Lock,
      text: "Enterprise-grade security"
    }];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const features = t0;
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <p className="text-lg text-muted-foreground max-w-md lg:max-w-2xl mx-auto lg:mx-0">Sign in securely with a magic link sent to your email or mobile. No passwords to remember.</p>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <>{t1}<div className="space-y-3 pt-4">{features.map(_temp)}</div></>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}

// Branded hero rendered on the left side of the page (stacks above the
// card on mobile). Shows the child org's logo and the markdown
// `landing_page_content` authored in Admin → Org Settings → Customer
// Experience → Landing page. The org name is no longer rendered as a
// fixed title — the heading is authored via the landing content so it
// can be edited or removed (GitLab #714). When no landing content is
// configured, falls back to the original welcome copy + feature bullets
// so the page never looks empty.
//
// The copy widens to `max-w-2xl` on `lg+` so it uses the space between
// the hero and the sign-in card rather than wrapping into a narrow,
// squashed column (GitLab #796).
function _temp(feature) {
  return <div key={feature.text} className="flex items-center gap-3 justify-center lg:justify-start"><div className="h-8 w-8 rounded-full flex items-center justify-center" style={{
      backgroundColor: "rgb(var(--org-primary-rgb) / 0.1)"
    }}><feature.icon className="h-4 w-4" style={{
        color: "var(--org-primary)"
      }} /></div><span className="text-sm text-muted-foreground">{feature.text}</span></div>;
}
function HeroSection(t0) {
  const $ = _c(12);
  const {
    organization
  } = t0;
  const organizationName = organization?.brand_name || organization?.name;
  const landingContent = organization?.landing_page_content;
  let t1;
  if ($[0] !== landingContent) {
    t1 = landingContent && landingContent.trim() !== "";
    $[0] = landingContent;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const hasLanding = !!t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="inline-flex items-center gap-2 glass-subtle rounded-full px-4 py-2"><Sparkles className="h-4 w-4" style={{
        color: "var(--org-primary)"
      }} /><span className="text-sm">{"Secure & Simple Access"}</span></div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== organization || $[4] !== organizationName) {
    t3 = organization?.brand_logo_url ? <img src={organization.brand_logo_url} alt={organizationName ?? "Organisation logo"} className="h-16 mx-auto lg:mx-0 drop-shadow-lg object-contain max-w-[260px]" /> : null;
    $[3] = organization;
    $[4] = organizationName;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== hasLanding || $[7] !== landingContent) {
    t4 = hasLanding ? <div className="max-w-md lg:max-w-2xl mx-auto lg:mx-0" data-testid="customer-login-landing-content"><MarkdownText content={landingContent as string} variant="large" className="text-muted-foreground" /></div> : <FallbackHeroBody />;
    $[6] = hasLanding;
    $[7] = landingContent;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t3 || $[10] !== t4) {
    t5 = <div className="flex-1 text-center lg:text-left space-y-6">{t2}{t3}{t4}</div>;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}
interface OrganizationBranding {
  id: string;
  name: string;
  parent_org_id: string | null;
  brand_name?: string;
  brand_logo_url?: string;
  background_image_url?: string;
  favicon_url?: string;
  primary_color?: string;
  secondary_color?: string;
  // Markdown blurb authored in Admin → Customer Experience → Landing
  // page. Rendered on the left side of the branded customer login.
  landing_page_content?: string | null;
  // Org features — only `direct_referral` is read here, to decide whether
  // to surface the "Create account" self-registration CTA.
  settings?: {
    features?: {
      direct_referral?: {
        enabled?: boolean;
        default_workflow_id?: string | null;
      };
    };
    invitation_channel_mode?: InvitationChannelMode;
    [key: string]: unknown;
  } | null;
}
function CustomerLoginContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const params = useParams();
  const pathname = usePathname();
  const orgSlug = params['org-slug'] as string;
  // On a tenant subdomain the middleware rewrites `/customer/login` to the
  // internal `[org-slug]` route while keeping the browser URL slug-less.
  // Detect that so outbound links (e.g. `Create account`) can omit the
  // redundant `/<slug>` suffix. On the apex, `pathname` still includes the
  // slug and we keep the legacy path form.
  const isOnTenantSubdomain = pathname === '/customer/login';
  const redirectTo = searchParams.get('redirect') || '/customer/dashboard';
  const declinedTerms = searchParams.get('declined') === '1';
  const inactivityLogout = searchParams.get('reason') === 'inactivity';

  // Issue #853 — the modal content is captured once on mount and `?verified=`
  // is then stripped from the URL, so a later full reload (e.g. the one
  // ConsentProvider triggers after first-time optional cookie acceptance)
  // does not re-open the success modal.
  const [verifiedModal] = useState(() => {
    const verifiedParam = searchParams.get('verified');
    return verifiedParam === '1' ? {
      title: 'Email Updated',
      message: 'Your email address has been updated. Please sign in with your new email.'
    } : verifiedParam === 'phone' ? {
      title: 'Phone Number Updated',
      message: 'Your phone number has been updated. Please sign in with your new mobile number.'
    } : null;
  });
  const [verifiedModalOpen, setVerifiedModalOpen] = useState(verifiedModal !== null);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    if (!url.searchParams.has('verified')) return;
    url.searchParams.delete('verified');
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
  }, []);

  // Issue #779 — invitations issued to an *existing* customer point at this
  // login page rather than the `/r` referral link, which prefills from the
  // invitation itself. Carry the username across on `?username=` so the
  // customer is not left guessing which identifier they signed up with.
  // The internal `sms-…@customer-login.invalid` shadow email is never a
  // valid sign-in identifier, so it is discarded rather than shown.
  const usernameParam = (searchParams.get('username') ?? '').trim();
  const prefilledUsername = usernameParam && usernameParam.length <= 254 && !isPhoneShadowEmail(usernameParam) ? usernameParam : '';
  const [email, setEmail] = useState(prefilledUsername);
  // Model 2 (`password_plus_factor`) only. Kept in memory so a resend can
  // repeat the password step without asking again.
  const [password, setPassword] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  // Channel the send resolved to. `sms` swaps the "check your email" success
  // screen for the in-browser 6-digit code-entry step; `email` keeps the
  // existing magic-link journey unchanged.
  const [channel, setChannel] = useState<'email' | 'sms'>('email');
  // #652 model 3 — the second factor, after the first one has passed.
  // `?step=sms` arrives from the first-factor magic link.
  const [secondStep, setSecondStep] = useState<'email' | 'sms' | null>(() => searchParams.get('step') === 'sms' ? 'sms' : null);
  // #652 model 2 forgot password. `?reset_token=` arrives from the emailed
  // reset link; it is captured once and stripped from the URL below.
  const [resetToken] = useState(() => searchParams.get(RESET_LINK_PARAM));
  const [forgotPassword, setForgotPassword] = useState(() => !!searchParams.get(RESET_LINK_PARAM));
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const url_0 = new URL(window.location.href);
    if (!url_0.searchParams.has(RESET_LINK_PARAM)) return;
    url_0.searchParams.delete(RESET_LINK_PARAM);
    window.history.replaceState(window.history.state, '', `${url_0.pathname}${url_0.search}${url_0.hash}`);
  }, []);
  const [error, setError] = useState('');
  // Set when /api/auth/magic-link/send responds with `code: 'customer_inactive'`
  // for a deactivated user. Surfaces a dedicated "contact your provider"
  // notice in place of the magic-link journey so the customer is told what
  // to do at submit time, rather than after clicking a link that fails at
  // verification. Wording is intentionally generic — see route handler.
  const [inactiveAccount, setInactiveAccount] = useState(false);
  const [organization, setOrganization] = useState<OrganizationBranding | null>(null);
  const [loadingOrg, setLoadingOrg] = useState(true);
  const [orgNotFound, setOrgNotFound] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);
  // When an existing session is present we surface a "you're already
  // signed in" prompt instead of silently redirecting — this lets staff
  // (or another customer) sign out and switch accounts without first
  // bouncing through `/customer/dashboard` only to be sent elsewhere by
  // middleware.
  const [signedInUser, setSignedInUser] = useState<{
    email: string;
    isAdmin: boolean;
  } | null>(null);
  const [signingOut, setSigningOut] = useState(false);
  useEffect(() => {
    const errorParam = searchParams.get('error');
    if (errorParam) {
      setError(decodeURIComponent(errorParam));
    }
  }, [searchParams]);

  // Fetch organisation branding by slug.
  //
  // Branded customer login is single-tenanted at the *child* org level —
  // the inverse of the admin rule. Customers are invited into specific
  // child organisations (eg `pennywise`), so:
  //
  //   - child org (`parent_org_id` set) → render the branded form here.
  //   - root parent org (`parent_org_id IS NULL`) → `router.replace` to the
  //     slug-less `/customer/login` notice ("use the link in your email"),
  //     preserving any query string (eg `?error=…`, `?redirect=…`). Parent
  //     orgs are administrative; customers should never sign in there.
  //   - unknown slug → "organisation not found".
  //
  // Note: only one parent-walk hop is needed — we don't walk further up,
  // because we redirect to the *slug-less* surface, not to a parent slug.
  useEffect(() => {
    const fetchOrganization = async () => {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const {
          data,
          error: error_0
        } = await (supabase as any).from('organizations').select('id, name, parent_org_id, brand_name, brand_logo_url, background_image_url, favicon_url, primary_color, secondary_color, landing_page_content, settings').eq('slug', orgSlug).single();
        if (error_0 || !data) {
          setOrgNotFound(true);
          setLoadingOrg(false);
          return;
        }
        if (!data.parent_org_id) {
          // Root parent org — bounce to the slug-less notice. Keep
          // `loadingOrg` true so the branded page never flashes.
          const qs = searchParams.toString();
          router.replace(`/customer/login${qs ? `?${qs}` : ''}`);
          return;
        }
        setOrganization(data);
        setLoadingOrg(false);
      } catch (err) {
        console.error('Error fetching organization:', err);
        setOrgNotFound(true);
        setLoadingOrg(false);
      }
    };
    fetchOrganization();
  }, [orgSlug, router, searchParams]);
  const brandStyle = buildBrandStyle(organization?.primary_color || '#0f766e', organization?.secondary_color || '#f97316');

  // Show the organisation's custom favicon in the browser tab (#551).
  useFavicon(organization?.favicon_url);

  // Identity-field copy follows the child org's invitation channel so an
  // email-only client never prompts for a mobile number (and vice versa).
  const identityCopy = getIdentityFieldCopy(organization?.settings?.invitation_channel_mode);
  // Single-sourced magic-link expiry copy — derived from the org's clamped
  // `email_magic_link_expiry_minutes` so on-screen text can't drift from the
  // real enforced expiry (defaults to 60 → "1 hour").
  const magicLinkExpiryCopy = formatExpiryMinutes(getAuthChallengeSettings(organization?.settings).email_magic_link_expiry_minutes);
  // Channel-aware button/heading copy — reacts to the typed identifier so a
  // mobile number shows code wording ("Text me a code") rather than "magic link".
  const journeyCopy = getAuthJourneyCopy(organization?.settings?.invitation_channel_mode, email);
  // #652 — model 2 asks for a password first, then sends the second factor.
  const passwordModel = getCustomerAuthModel(organization?.settings) === 'password_plus_factor';
  const showForgotPassword = passwordModel && forgotPassword && !!organization?.id;
  // Self-registration ("Create account") is only offered when the child org
  // has direct referrals enabled AND a default workflow configured — the same
  // gate enforced server-side by /api/referrals/self-register.
  const directReferral = organization?.settings?.features?.direct_referral;
  const selfRegistrationEnabled = Boolean(directReferral?.enabled && directReferral?.default_workflow_id);
  const registerHref = `${isOnTenantSubdomain ? '/customer/register' : `/customer/register/${orgSlug}`}${searchParams.get('redirect') ? `?redirect=${encodeURIComponent(searchParams.get('redirect') as string)}` : ''}`;

  // Detect an existing session and decide whether the visitor is an
  // admin (has at least one `user_role_assignments` row — matches the
  // middleware rule) or a customer. We surface a prompt instead of
  // redirecting so staff who land here by accident can clearly see whose
  // session is active and switch accounts in one click.
  useEffect(() => {
    const detect = async () => {
      try {
        const {
          data: {
            user
          }
        } = await supabase.auth.getUser();
        if (!user) {
          setSignedInUser(null);
          return;
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const {
          data: assignment
        } = await (supabase as any).from('user_role_assignments').select('id').eq('user_id', user.id).limit(1).maybeSingle();
        setSignedInUser({
          email: user.email ?? '',
          isAdmin: !!assignment
        });
      } catch (err_0) {
        console.error('Error detecting existing session:', err_0);
        setSignedInUser(null);
      }
    };
    detect();
  }, []);
  const handleSignOut = async () => {
    setSigningOut(true);
    try {
      await supabase.auth.signOut();
      setSignedInUser(null);
    } catch (err_1) {
      console.error('Error signing out:', err_1);
    } finally {
      setSigningOut(false);
    }
  };
  const handleContinueAsCurrentUser = () => {
    if (!signedInUser) return;
    // Admins can't access /customer/dashboard (middleware blocks them),
    // so hand them off to the admin surface instead. Customers get the
    // requested redirect target.
    router.push(signedInUser.isAdmin ? '/admin' : redirectTo);
  };
  const postPasswordSignIn = (token: string | null) => fetch('/api/auth/customer-password/sign-in', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      username: email,
      password,
      redirectTo,
      organizationId: organization?.id,
      orgBrandName: organization?.brand_name || organization?.name,
      orgLogoUrl: organization?.brand_logo_url,
      orgPrimaryColor: organization?.primary_color,
      captchaToken: token
    })
  });
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setInactiveAccount(false);
    setSending(true);
    try {
      const response = passwordModel ? await postPasswordSignIn(captchaToken) : await fetch('/api/auth/magic-link/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email,
          redirectTo: redirectTo.startsWith('http') ? redirectTo : `${window.location.origin}${redirectTo}`,
          organizationId: organization?.id,
          orgBrandName: organization?.brand_name || organization?.name,
          orgLogoUrl: organization?.brand_logo_url,
          orgPrimaryColor: organization?.primary_color,
          captchaToken
        })
      });
      const result = await response.json();
      if (!response.ok) {
        // Deactivated customers are blocked at send time. Render a dedicated
        // notice instead of the inline form error so the message stands out
        // and the customer is told to contact their provider organisation.
        if (result?.code === 'customer_inactive') {
          setInactiveAccount(true);
          setError(result.error || 'We are unable to sign you in right now. Please contact your provider organisation for assistance.');
          setCaptchaToken(null);
          setTurnstileResetKey(k_0 => k_0 + 1);
          return;
        }
        throw new Error(result.error);
      }
      // `channel` is derived server-side from the submitted identifier and
      // returned uniformly (no enumeration oracle). `sms` shows the code step.
      setChannel(result?.channel === 'sms' ? 'sms' : 'email');
      setSent(true);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err_2: any) {
      console.error('Error sending magic link:', err_2);
      setError(err_2.message || 'Failed to send magic link');
      setCaptchaToken(null);
      setTurnstileResetKey(k => k + 1);
    } finally {
      setSending(false);
    }
  };

  // Re-send an SMS OTP from the code-entry step, using a fresh Turnstile token
  // (tokens are single-use, so the code step obtains its own). Mirrors the send
  // body above; the caller surfaces any error and resets the widget.
  const resendSmsCode = async (token_0: string) => {
    const response_0 = passwordModel ? await postPasswordSignIn(token_0) : await fetch('/api/auth/magic-link/send', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        email,
        redirectTo: redirectTo.startsWith('http') ? redirectTo : `${window.location.origin}${redirectTo}`,
        organizationId: organization?.id,
        orgBrandName: organization?.brand_name || organization?.name,
        orgLogoUrl: organization?.brand_logo_url,
        orgPrimaryColor: organization?.primary_color,
        captchaToken: token_0
      })
    });
    const result_0 = await response_0.json();
    if (!response_0.ok) throw new Error(result_0.error || 'Failed to resend code');
  };
  const resendSecondFactor = async (token_1: string) => {
    const response_1 = await fetch('/api/auth/customer-challenge/resend', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        organizationId: organization?.id,
        redirectTo,
        captchaToken: token_1
      })
    });
    const result_1 = await response_1.json();
    if (!response_1.ok) throw new Error(result_1.error || 'Failed to resend code');
  };
  if (loadingOrg) {
    return <div className="min-h-screen flex items-center justify-center px-4" style={brandStyle}>
        <AnimatedBackground />
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>;
  }

  // If the org supplies a `background_image_url`, use it as a full-bleed
  // cover image and suppress the animated gradient (matches the admin
  // branded login). Otherwise fall back to the animated gradient shell.
  const backgroundImageUrl = organization?.background_image_url;
  // The legal footer belongs on every customer surface, not only the ones
  // behind sign-in: this page is where a customer first hands over an email
  // address, so it is where the privacy and cookie policies must be reachable.
  const footerLegalContent = getOrgLegalContent(organization?.settings);
  const shellStyle: React.CSSProperties = {
    ...brandStyle,
    ...(backgroundImageUrl ? {
      backgroundImage: `url(${backgroundImageUrl})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundAttachment: 'fixed'
    } : {})
  };
  if (orgNotFound) {
    return <div className="min-h-screen flex flex-col" style={shellStyle}>
        {!backgroundImageUrl && <AnimatedBackground />}
        <div className="fixed top-4 right-4 z-50">
          <ThemeToggle />
        </div>
        <div className="flex-1 flex items-center justify-center px-6 py-12">
          <div className="container mx-auto">
            <div className="flex flex-col lg:flex-row items-start gap-12 lg:gap-16">
              <HeroSection organization={organization} />
              <div className="flex-1 w-full max-w-md mx-auto">
                <GlassCard className="w-full" variant="strong">
                  <GlassCardHeader className="space-y-1 text-center">
                    <div className="mx-auto h-12 w-12 rounded-full bg-red-500/10 flex items-center justify-center mb-2">
                      <Mail className="h-6 w-6 text-red-500" />
                    </div>
                    <GlassCardTitle className="text-2xl font-bold">
                      Organisation Not Found
                    </GlassCardTitle>
                    <GlassCardDescription>
                      The organisation &quot;{orgSlug}&quot; could not be found.
                    </GlassCardDescription>
                  </GlassCardHeader>
                  <GlassCardContent className="space-y-4">
                    <p className="text-sm text-muted-foreground text-center">
                      Please check the link you were provided or contact your administrator.
                    </p>
                  </GlassCardContent>
                </GlassCard>
              </div>
            </div>
          </div>
        </div>
        <PoweredByPaylinkFooter className="px-6 pb-8" legalContent={footerLegalContent} />
      </div>;
  }
  const orgDisplayName = organization?.brand_name || organization?.name;

  // Existing session — show a clear "you're signed in as X" prompt
  // rather than silently bouncing through `/customer/dashboard`. This
  // covers the common case of an admin (or another customer) clicking a
  // branded customer login link while a different session is still
  // active.
  if (signedInUser) {
    return <div className="min-h-screen flex flex-col" style={shellStyle}>
        {!backgroundImageUrl && <AnimatedBackground />}
        <div className="fixed top-4 right-4 z-50">
          <ThemeToggle />
        </div>
        <div className="flex-1 flex items-center justify-center px-6 py-12">
          <div className="container mx-auto">
            <div className="flex flex-col lg:flex-row items-start gap-12 lg:gap-16">
              <HeroSection organization={organization} />
              <div className="flex-1 w-full max-w-md mx-auto">
                <GlassCard className="w-full rounded-[24px] border border-white/20" variant="strong">
                  <GlassCardHeader className="space-y-1 text-center">
                    {/* Brand logo lives in the left hero column; the card
                        uses a neutral icon so the logo isn't duplicated. */}
                    <div className="mx-auto h-12 w-12 rounded-full flex items-center justify-center mb-2" style={{
                    background: 'linear-gradient(135deg, rgb(var(--org-primary-rgb) / 0.2), rgb(var(--org-secondary-rgb) / 0.2))'
                  }}>
                      <UserCircle2 className="h-6 w-6" style={{
                      color: 'var(--org-primary)'
                    }} />
                    </div>
                    <GlassCardTitle className="text-2xl font-bold">
                      You&apos;re already signed in
                    </GlassCardTitle>
                    <GlassCardDescription>
                      Signed in as <span className="font-medium">{signedInUser.email}</span>
                      {signedInUser.isAdmin && <> &middot; staff account</>}
                    </GlassCardDescription>
                  </GlassCardHeader>
                  <GlassCardContent className="space-y-4">
                    <p className="text-sm text-muted-foreground text-center">
                      {signedInUser.isAdmin ? `You can't access ${orgDisplayName ?? 'this organisation'}'s customer area with a staff account. Sign out to sign in as a customer.` : 'Continue to your dashboard, or sign out to sign in as a different customer.'}
                    </p>
                    <div className="flex flex-col gap-3">
                      <Button type="button" onClick={handleContinueAsCurrentUser} className="w-full h-11" disabled={signingOut}>
                        {signedInUser.isAdmin ? 'Go to Admin Panel' : 'Continue to Dashboard'}
                      </Button>
                      <Button type="button" variant="outline" onClick={handleSignOut} className="w-full h-11 glass" disabled={signingOut}>
                        {signingOut ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Signing out...</> : <><LogOut className="mr-2 h-4 w-4" /> Sign out &amp; use a different account</>}
                      </Button>
                    </div>
                  </GlassCardContent>
                </GlassCard>
              </div>
            </div>
          </div>
        </div>
        <PoweredByPaylinkFooter className="px-6 pb-8" legalContent={footerLegalContent} />
      </div>;
  }
  return <div className="min-h-screen flex flex-col" style={shellStyle}>
      {!backgroundImageUrl && <AnimatedBackground />}
      <div className="fixed top-4 right-4 z-50">
        <ThemeToggle />
      </div>

      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="container mx-auto">
          <div className="flex flex-col lg:flex-row items-start gap-12 lg:gap-16">
            <HeroSection organization={organization} />

            <div className="flex-1 w-full max-w-md mx-auto">
              <div className="absolute inset-0 rounded-[40px] blur-3xl -z-10 hidden lg:block" style={{
              background: 'linear-gradient(135deg, rgb(var(--org-primary-rgb) / 0.2), rgb(var(--org-secondary-rgb) / 0.1), rgb(var(--org-primary-rgb) / 0.2))'
            }} />
              <GlassCard className="w-full rounded-[24px] border border-white/20" variant="strong">
                <GlassCardHeader className="space-y-1 text-center">
                  {/* The SMS code-entry step renders its own icon + heading, so
                      the card header is suppressed there to avoid duplication. */}
                  {!(sent && channel === 'sms') && secondStep !== 'sms' && !showForgotPassword && <>
                      {/* Brand logo lives in the left hero column; the card
                          uses a neutral icon so the logo isn't duplicated. */}
                      <div className="mx-auto h-12 w-12 rounded-full flex items-center justify-center mb-2" style={{
                    background: 'linear-gradient(135deg, rgb(var(--org-primary-rgb) / 0.2), rgb(var(--org-secondary-rgb) / 0.2))'
                  }}>
                        <Sparkles className="h-6 w-6" style={{
                      color: 'var(--org-primary)'
                    }} />
                      </div>
                      <GlassCardTitle className="text-2xl font-bold">
                        {sent || secondStep === 'email' ? 'Check Your Email!' : 'Sign In'}
                      </GlassCardTitle>
                      <GlassCardDescription>
                        {secondStep === 'email' ? "We've sent a secure login link to your registered email address" : sent ? `We've sent a secure login link to ${email}` : organization ? `Enter your ${identityCopy.prompt}${passwordModel ? ' and password' : ''} to access your account` : `Enter your ${identityCopy.prompt} to receive ${journeyCopy.receivePhrase}`}
                      </GlassCardDescription>
                    </>}
                </GlassCardHeader>
                <GlassCardContent>
                  {inactivityLogout && !sent && <div role="status" className="mb-4 rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-700 dark:text-amber-400">
                      You were signed out due to inactivity. Please sign in again.
                    </div>}
                  {declinedTerms && !sent && <div role="status" className="mb-4 rounded-md border border-border/60 bg-muted/40 p-3 text-sm text-muted-foreground">
                      You declined the latest Terms &amp; Conditions. Sign in again when
                      you&apos;re ready to accept them.
                    </div>}
                  {showForgotPassword ? <CustomerForgotPassword organizationId={organization?.id ?? ''} policy={getCustomerPasswordPolicy(organization?.settings)} initialUsername={email} resetToken={resetToken} brand={{
                  name: organization?.brand_name || organization?.name,
                  logoUrl: organization?.brand_logo_url,
                  primaryColor: organization?.primary_color
                }} identityLabel={identityCopy.label} buttonClassName="w-full h-11 text-white border-0 hover:opacity-90 transition-opacity" buttonStyle={{
                  background: 'linear-gradient(to right, var(--org-primary), var(--org-secondary))'
                }} onBack={() => {
                  setForgotPassword(false);
                  setPassword('');
                  setError('');
                }} /> : secondStep === 'sms' ? <SmsCodeEntry phone="" displayPhone="your registered mobile number" organizationId={organization?.id ?? ''} redirectTo={redirectTo} brandName={organization?.brand_name || organization?.name} buttonClassName="w-full h-11 text-white border-0 hover:opacity-90 transition-opacity" buttonStyle={{
                  background: 'linear-gradient(to right, var(--org-primary), var(--org-secondary))'
                }} onResend={resendSecondFactor} onUseDifferent={() => {
                  setSecondStep(null);
                  setSent(false);
                  setChannel('email');
                }} verifyEndpoint="/api/auth/customer-challenge/verify" /> : secondStep === 'email' ? <div className="text-center py-4" data-testid="second-step-email">
                      <div className="mx-auto h-16 w-16 rounded-full bg-green-500/10 flex items-center justify-center mb-4">
                        <CheckCircle className="h-10 w-10 text-green-500" />
                      </div>
                      <p role="status" className="text-muted-foreground mb-4">
                        Your code has been accepted. Open the link we&apos;ve sent to your registered email
                        address to finish signing in. The link will expire in {magicLinkExpiryCopy}.
                      </p>
                      <Button variant="outline" onClick={() => {
                    setSecondStep(null);
                    setSent(false);
                    setEmail('');
                    setChannel('email');
                  }} className="mt-2">
                        Start again
                      </Button>
                    </div> : !sent ? <form onSubmit={handleSubmit} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="email">{identityCopy.label}</Label>
                        <Input id="email" type="text" placeholder={identityCopy.placeholder} value={email} onChange={e_0 => {
                      setEmail(e_0.target.value);
                      if (inactiveAccount) {
                        setInactiveAccount(false);
                        setError('');
                      }
                    }} required disabled={sending} autoComplete="username" className="h-11" />
                      </div>

                      {passwordModel && <div className="space-y-1">
                          <CustomerPasswordInput mode="enter" value={password} onChange={setPassword} disabled={sending} />
                          <div className="flex justify-end">
                            <button type="button" data-testid="forgot-password-link" onClick={() => {
                        setError('');
                        setForgotPassword(true);
                      }} disabled={sending} className="text-sm underline underline-offset-4 text-muted-foreground hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 rounded">
                              Forgot your password?
                            </button>
                          </div>
                        </div>}

                      {inactiveAccount ? <div role="alert" data-testid="login-inactive-error" className="rounded-md border border-amber-300/60 bg-amber-50 dark:bg-amber-900/20 p-3 text-sm text-amber-900 dark:text-amber-200">
                          {error}
                        </div> : error && <div role="alert" className="text-sm text-red-600 bg-red-50 dark:bg-red-900/20 p-3 rounded-md">
                            {error}
                          </div>}

                      <TurnstileWidget onSuccess={token_2 => setCaptchaToken(token_2)} onError={() => setCaptchaToken(null)} onExpire={() => setCaptchaToken(null)} resetKey={turnstileResetKey} className="flex justify-center" />

                      <Button type="submit" className="w-full h-11 text-white border-0 hover:opacity-90 transition-opacity" style={{
                    background: 'linear-gradient(to right, var(--org-primary), var(--org-secondary))'
                  }} disabled={sending || !captchaToken}>
                        {sending ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {passwordModel ? 'Signing in...' : 'Sending...'}</> : passwordModel ? <><Lock className="mr-2 h-4 w-4" /> Sign in</> : <><Mail className="mr-2 h-4 w-4" /> {journeyCopy.buttonLabel}</>}
                      </Button>

                      {selfRegistrationEnabled && <div className="pt-2">
                          <div className="relative my-2">
                            <div className="absolute inset-0 flex items-center" aria-hidden="true">
                              <span className="w-full border-t border-border/60" />
                            </div>
                            <div className="relative flex justify-center text-xs">
                              <span className="bg-transparent px-2 text-muted-foreground">New here?</span>
                            </div>
                          </div>
                          <Button asChild type="button" variant="outline" className="w-full h-11 glass">
                            <Link href={registerHref} data-testid="create-account-cta">
                              <UserPlus className="mr-2 h-4 w-4" /> Create account
                            </Link>
                          </Button>
                        </div>}
                    </form> : channel === 'sms' ? <SmsCodeEntry phone={email} organizationId={organization?.id ?? ''} redirectTo={redirectTo} brandName={organization?.brand_name || organization?.name} buttonClassName="w-full h-11 text-white border-0 hover:opacity-90 transition-opacity" buttonStyle={{
                  background: 'linear-gradient(to right, var(--org-primary), var(--org-secondary))'
                }} onResend={resendSmsCode} onUseDifferent={() => {
                  setSent(false);
                  setEmail('');
                  setPassword('');
                  setChannel('email');
                }} verifyEndpoint={passwordModel ? '/api/auth/customer-challenge/verify' : undefined} onNextStep={() => setSecondStep('email')} /> : <div className="text-center py-4">
                      <div className="mx-auto h-16 w-16 rounded-full bg-green-500/10 flex items-center justify-center mb-4">
                        <CheckCircle className="h-10 w-10 text-green-500" />
                      </div>
                      <p className="text-muted-foreground mb-4">
                        Click the link in the message to sign in. The link will expire in {magicLinkExpiryCopy}.
                      </p>
                      {/* Neutral notice shown to everyone — covers the case where
                          a deactivated customer never receives an email (the send
                          endpoint silently succeeds for inactive users) without
                          leaking whether the address belongs to an active,
                          inactive, or non-existent account. */}
                      {(() => {
                    const providerName = organization?.brand_name || organization?.name;
                    return <p className="text-xs text-muted-foreground mb-4">
                            Didn&apos;t receive a message? If your account is inactive, please contact{' '}
                            {providerName ? <span className="font-medium">{providerName}</span> : 'your provider'}
                            {' '}for help restoring access.
                          </p>;
                  })()}
                      <Button variant="outline" onClick={() => {
                    setSent(false);
                    setEmail('');
                    setPassword('');
                  }} className="mt-2">
                        Use a different email or mobile
                      </Button>
                    </div>}
                </GlassCardContent>
              </GlassCard>
            </div>
          </div>
        </div>
      </div>
      <PoweredByPaylinkFooter className="px-6 pb-8" legalContent={footerLegalContent} />
      {verifiedModal && <AlertModal isOpen={verifiedModalOpen} onClose={() => setVerifiedModalOpen(false)} title={verifiedModal.title} message={verifiedModal.message} variant="info" />}
    </div>;
}
export default function BrandedCustomerLoginPage() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Suspense fallback={<div className="min-h-screen flex items-center justify-center" style={buildBrandStyle("#0f766e", "#f97316")}><AnimatedBackground /><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>}><CustomerLoginContent /></Suspense>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}